import React, { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    Paper,
    useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTopicLabel } from './useTopicLabel';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { fetchTopicCandidateBreakdown } from '../../api/podcasts';
import { getColorForCandidate } from './electionColors';

interface TopicBreakdownTabProps {
    selectedRange: {
        from: string;
        to: string;
    };
    includeRadio: boolean;
    isActive: boolean;
}

export default function TopicBreakdownTab({ selectedRange, includeRadio, isActive }: TopicBreakdownTabProps) {
    const theme = useTheme();
    const { t } = useTranslation('elecciones');
    const topicLabel = useTopicLabel();
    const [userSelectedCandidates, setUserSelectedCandidates] = useState<string[] | null>(null);
    const [userSelectedTopics, setUserSelectedTopics] = useState<string[] | null>(null);

    const { data: breakdownData, isLoading, error } = useQuery({
        queryKey: ['topic-candidate-breakdown', selectedRange.from, selectedRange.to, includeRadio],
        queryFn: () => fetchTopicCandidateBreakdown({
            from: selectedRange.from,
            to: selectedRange.to,
            includeRadio,
        }),
        enabled: isActive,
        staleTime: 5 * 60 * 1000,
    });

    // Get all topics
    const allTopics = useMemo(() => {
        if (!breakdownData?.topics) return [];
        return breakdownData.topics.map(t => t.topic);
    }, [breakdownData]);

    // Get all candidates sorted by total mentions across all topics
    const allCandidates = useMemo(() => {
        if (!breakdownData?.topics) return [];

        const candidateTotals = new Map<string, number>();
        breakdownData.topics.forEach(topic => {
            Object.entries(topic.candidates).forEach(([name, data]) => {
                candidateTotals.set(name, (candidateTotals.get(name) || 0) + data.count);
            });
        });

        return Array.from(candidateTotals.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name);
    }, [breakdownData]);

    // Derive effective selected candidates (user selection or default)
    const selectedCandidates = useMemo(() => {
        if (userSelectedCandidates !== null) return userSelectedCandidates;
        return allCandidates.slice(0, 5);
    }, [userSelectedCandidates, allCandidates]);

    // Derive effective selected topics (user selection or default)
    const selectedTopics = useMemo(() => {
        if (userSelectedTopics !== null) return userSelectedTopics;
        return allTopics;
    }, [userSelectedTopics, allTopics]);

    const setSelectedCandidates = setUserSelectedCandidates;
    const setSelectedTopics = setUserSelectedTopics;

    // Filter topics based on selection
    const filteredTopics = useMemo(() => {
        if (!breakdownData?.topics) return [];
        if (selectedTopics.length === 0) return breakdownData.topics;
        return breakdownData.topics.filter(t => selectedTopics.includes(t.topic));
    }, [breakdownData, selectedTopics]);

    // Transform data for stacked area chart (filtered)
    const chartData = useMemo(() => {
        if (!filteredTopics.length) return [];

        return filteredTopics.map(topic => {
            const dataPoint: any = {
                topic: topicLabel(topic.topic),
                totalMentions: topic.totalMentions,
            };

            // Add each selected candidate's percentage
            Object.entries(topic.candidates).forEach(([candidateName, data]) => {
                if (selectedCandidates.includes(candidateName)) {
                    dataPoint[candidateName] = data.percentage;
                    dataPoint[`${candidateName}_count`] = data.count;
                }
            });

            return dataPoint;
        });
    }, [filteredTopics, selectedCandidates, topicLabel]);

    // Get displayed candidates (filtered and sorted)
    const displayedCandidates = useMemo(() => {
        return allCandidates.filter(c => selectedCandidates.includes(c));
    }, [allCandidates, selectedCandidates]);

    // Calculate overall stats for filtered data
    const totalMentions = useMemo(() => {
        return filteredTopics.reduce((sum, t) => sum + t.totalMentions, 0) || 0;
    }, [filteredTopics]);

    const handleCandidateChange = (event: any) => {
        const value = event.target.value;
        setSelectedCandidates(typeof value === 'string' ? value.split(',') : value);
    };

    const handleTopicChange = (event: any) => {
        const value = event.target.value;
        setSelectedTopics(typeof value === 'string' ? value.split(',') : value);
    };

    if (!isActive) {
        return null;
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                {t('empty.loadFailed', { message: (error as Error).message })}
            </Alert>
        );
    }

    if (!chartData.length) {
        return (
            <Alert severity="info">
                {t('empty.noPeriod')}
            </Alert>
        );
    }

    return (
        <Box>
            {/* Filters */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
                {/* Candidate Filter */}
                <FormControl sx={{ flex: 1 }}>
                    <InputLabel>{t('fields.candidates')}</InputLabel>
                    <Select
                        multiple
                        value={selectedCandidates}
                        onChange={handleCandidateChange}
                        input={<OutlinedInput label={t('fields.candidates')} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.slice(0, 3).map((value) => (
                                    <Chip
                                        key={value}
                                        label={value}
                                        size="small"
                                        sx={{
                                            backgroundColor: getColorForCandidate(value, allCandidates.indexOf(value)),
                                            color: 'white'
                                        }}
                                    />
                                ))}
                                {selected.length > 3 && (
                                    <Chip label={`+${selected.length - 3}`} size="small" />
                                )}
                            </Box>
                        )}
                    >
                        {allCandidates.map((candidate, idx) => (
                            <MenuItem key={candidate} value={candidate}>
                                <Checkbox checked={selectedCandidates.includes(candidate)} />
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: getColorForCandidate(candidate, idx),
                                        mr: 1
                                    }}
                                />
                                <ListItemText primary={candidate} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Topic Filter */}
                <FormControl sx={{ flex: 1 }}>
                    <InputLabel>{t('fields.topics')}</InputLabel>
                    <Select
                        multiple
                        value={selectedTopics}
                        onChange={handleTopicChange}
                        input={<OutlinedInput label={t('fields.topics')} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.slice(0, 3).map((value) => (
                                    <Chip key={value} label={value} size="small" />
                                ))}
                                {selected.length > 3 && (
                                    <Chip label={`+${selected.length - 3}`} size="small" />
                                )}
                            </Box>
                        )}
                    >
                        {allTopics.map((topic) => (
                            <MenuItem key={topic} value={topic}>
                                <Checkbox checked={selectedTopics.includes(topic)} />
                                <ListItemText primary={topic} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {/* Stats Summary */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        minWidth: 150,
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.05em' }}>
                        {t('breakdown.totalMentions')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, mt: 0.5 }}>
                        {totalMentions.toLocaleString()}
                    </Typography>
                </Paper>
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        minWidth: 150,
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.05em' }}>
                        {t('breakdown.topicsShown')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, mt: 0.5 }}>
                        {chartData.length} <Typography component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 400 }}>/ {allTopics.length}</Typography>
                    </Typography>
                </Paper>
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        minWidth: 150,
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.05em' }}>
                        {t('breakdown.candidatesShown')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, mt: 0.5 }}>
                        {displayedCandidates.length} <Typography component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 400 }}>/ {allCandidates.length}</Typography>
                    </Typography>
                </Paper>
            </Stack>

            {/* Description */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: 2,
                    backgroundColor: 'action.selected',
                    border: '1px solid #BBF7D0',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                }}
            >
                <Box sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: '#22C55E',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.25,
                }}>
                    <Typography sx={{ color: 'white', fontSize: 14 }}>📊</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: theme.palette.success.light }}>
                    {t('breakdown.explanation')}
                    {t('breakdown.example')}
                </Typography>
            </Paper>

            {/* Stacked Area Chart */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: '1px solid', borderColor: 'divider',
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{
                    px: 3,
                    py: 2.5,
                    borderBottom: 1, borderColor: 'divider',
                    bgcolor: 'action.hover',
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            fontSize: '1.1rem',
                            letterSpacing: '-0.01em',
                        }}
                    >
                        {t('charts.topicShare')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                        {t('breakdown.subtitle')}
                    </Typography>
                </Box>
                <Box sx={{ p: 3 }}>

                    <Box sx={{ width: '100%', height: 500 }}>
                        <ResponsiveContainer>
                            <AreaChart
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                <XAxis
                                    dataKey="topic"
                                    tick={{ fontSize: 11 }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                    interval={0}
                                />
                                <YAxis
                                    domain={[0, 100]}
                                    tickFormatter={(value) => `${value}%`}
                                    tick={{ fontSize: 11 }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary,
                                        border: '1px solid #ddd',
                                        borderRadius: 8
                                    }}
                                    formatter={(value, name, props) => {
                                        if ((name as string).endsWith('_count')) return null;
                                        const count = props.payload[`${name}_count`] || 0;
                                        return [t('breakdown.tooltip', { value, n: count }), name];
                                    }}
                                    labelFormatter={(label, payload) => {
                                        const total = payload?.[0]?.payload?.totalMentions || 0;
                                        return t('breakdown.tooltipTotal', { label, total });
                                    }}
                                />
                                <Legend />

                                {/* Stacked areas for each selected candidate */}
                                {displayedCandidates.map((candidate) => (
                                    <Area
                                        key={candidate}
                                        type="monotone"
                                        dataKey={candidate}
                                        stackId="1"
                                        fill={getColorForCandidate(candidate, allCandidates.indexOf(candidate))}
                                        stroke={getColorForCandidate(candidate, allCandidates.indexOf(candidate))}
                                        fillOpacity={0.8}
                                        name={candidate}
                                    />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>
            </Paper>

            {/* Candidate Legend with totals */}
            <Paper
                elevation={0}
                sx={{
                    mt: 3,
                    borderRadius: 3,
                    border: '1px solid', borderColor: 'divider',
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{
                    px: 3,
                    py: 2,
                    borderBottom: 1, borderColor: 'divider',
                    bgcolor: 'action.hover',
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            fontSize: '1rem',
                        }}
                    >
                        {t('charts.totalByCandidate')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {t('breakdown.sum')}
                    </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        {displayedCandidates.map((candidate) => {
                            const candidateTotal = filteredTopics.reduce(
                                (sum, t) => sum + (t.candidates[candidate]?.count || 0), 0
                            ) || 0;
                            const candidatePct = totalMentions > 0
                                ? Math.round((candidateTotal / totalMentions) * 1000) / 10
                                : 0;

                            return (
                                <Paper
                                    key={candidate}
                                    elevation={0}
                                    sx={{
                                        minWidth: 140,
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid', borderColor: 'divider',
                                        borderLeft: `4px solid ${getColorForCandidate(candidate, allCandidates.indexOf(candidate))}`,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                            transform: 'translateY(-2px)',
                                        }
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                                        {candidate.split(' ').slice(-1)[0]}
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                                        {candidateTotal.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                        {candidatePct}% del total
                                    </Typography>
                                </Paper>
                            );
                        })}
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
}

